import { useState } from "react";
import axios from "axios";
import api from "@/store/api";
import { getData } from "@/lib/utils";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";

const useTransform = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const [loading, setLoading] = useState(false);
  const [isTransformLoading, setIsLoading] = useState(false);
  const [transformError, setError] = useState<string | null>(null);
  const [error, setErrorTransform] = useState<string | null>(null);

  const upsertTransform = async (transformData: any) => {
    setIsLoading(true);
    setError(null);
    try {
      const Token = getData("tokens")?.accessToken;
      const formData = new FormData();
      Object.keys(transformData).forEach((key) => {
        if (key === "attachment" && transformData[key]) {
          formData.append(key, transformData[key]);
        } else {
          formData.append(key, transformData[key]);
        }
      });
      const response = await api.post(
        "/web/Transforms/upsert",
        formData,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (err) {
      const message =
        axios.isAxiosError(err) && err.response?.data?.message
          ? err.response.data.message
          : t("alerts.error.unexpectedError");
      setError(message);
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  };
  const updateTransform = async (transformData: any) => {
    setLoading(true);
    setError(null);

    try {
      const Token = getData("tokens")?.accessToken;
      const response = await api.post("/web/Transforms/update", transformData, {
        headers: {
          Authorization: `Bearer ${Token}`,
        },
      });
      return { success: true, data: response.data };
    } catch (err: any) {
      const message =
        axios.isAxiosError(err) && err.response?.data?.message
          ? err.response.data.message
          : t("alerts.error.unexpectedError");
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };



  // Fetch transforms by company or user ID
  const fetchTransforms = async (params: any) => {
    setLoading(true);
    setErrorTransform(null);
    try {
        const Token = getData("tokens")?.accessToken;
      const queryParams = new URLSearchParams(params).toString();
      const response = await api.get(`/web/Transforms/getAll?${queryParams}`,{headers:{
        Authorization:`Bearer ${Token}`
      }});
      setLoading(false);
      return response.data;
    } catch (err) {
      setLoading(false);
      if (axios.isAxiosError(err)) {
        setErrorTransform(err.response?.data?.message || "Error fetching transforms");
      } else {
        setErrorTransform(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };

  // Fetch a specific transform by ID
  const fetchTransformById = async (transformId: string) => {
    setLoading(true);
    setErrorTransform(null);
    try {
      const Token = getData("tokens")?.accessToken;
      const response = await api.get(`/web/Transforms/get?id=${transformId}`, {
        headers: {
          Authorization: `Bearer ${Token}`,
        },
      });
      setLoading(false);
      return response.data;
    } catch (err) {
      setLoading(false);
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 404) {
          return null; // Transform not found
        }
        setErrorTransform(err.response?.data?.message || "Error fetching transform");
      } else {
        setErrorTransform(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };

  // Delete a transform by ID
  const deleteTransform = async (transformId: string) => {
    setLoading(true);
    setErrorTransform(null);
    try {
      const Token = getData("tokens")?.accessToken;
      const response = await api.delete(`/web/Transforms/delete?id=${transformId}`, {
        headers: {
          Authorization: `Bearer ${Token}`,
        },
      });
      setLoading(false);
      return response.data;
    } catch (err) {
      setLoading(false);
      const message =
        axios.isAxiosError(err) && err.response?.data?.message
          ? err.response.data.message
          : t("alerts.error.unexpectedError");
      setErrorTransform(message);
      return { success: false, error: message };
    }
  };

  // Change the status of a transform
  const changeTransformStatus = async (statusData: any) => {
    setLoading(true);
    setErrorTransform(null);
    try {
      const Token = getData("tokens")?.accessToken;
      const response = await api.post(
        "/web/Transforms/changeStatus",
        statusData,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        }
      );
      setLoading(false);
      return response.data;
    } catch (err) {
      setLoading(false);
      const message =
        axios.isAxiosError(err) && err.response?.data?.message
          ? err.response.data.message
          : t("alerts.error.unexpectedError");
      setErrorTransform(message);
      return { success: false, error: message };
    }
  };

  return {
    upsertTransform,
    updateTransform,
    fetchTransforms,
    fetchTransformById,
    deleteTransform,
    changeTransformStatus,
    isTransformLoading,
    transformError,
    loading,
    error,
  };
};

export default useTransform;
